/**
 * The "where did I come from" back link, carried in router state.
 *
 * Captured ONCE per mount: moving between a subject's tabs replaces
 * `location.state`, and the back link must keep pointing at the screen you
 * actually arrived from rather than vanishing on the first tab click.
 *
 * The allowlist is the security boundary — `location.state` is
 * attacker-influenceable via a crafted link, so only known internal
 * destinations are ever rendered as a link target. Anything else is dropped.
 */
import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useT } from "@mmm/ui";
const BACK_TARGETS = ["/dashboard", "/subjects", "/inventory"] as const;

/**
 * A subject's Overview tab, which is where chapters are created and opened
 * from. It cannot be a fixed string like the others because it carries a
 * subject id, so it is matched by shape instead — anchored, and with an id
 * charset that admits no slashes, dots or colons. That keeps the allowlist a
 * real boundary: no traversal, no protocol-relative "//host", no other route.
 *
 * Needed because /subjects/:id on its own now lands on the map, so falling
 * back to it would send someone "back" to a different tab than they left.
 */
const SUBJECT_OVERVIEW_TARGET = /^\/subjects\/[A-Za-z0-9_-]+\/overview$/;

type BackTarget = (typeof BACK_TARGETS)[number] | `/subjects/${string}/overview`;

function isBackTarget(value: unknown): value is BackTarget {
  if (typeof value !== "string") return false;
  return (BACK_TARGETS as readonly string[]).includes(value) || SUBJECT_OVERVIEW_TARGET.test(value);
}

export interface BackLink {
  to: BackTarget;
  /** Accessible label — the control itself is an icon. */
  label: string;
}

export function useBackTo(): BackLink | null {
  const location = useLocation();
  const t = useT();
  const [to] = useState<BackTarget | null>(() => {
    const value = (location.state as { backTo?: unknown } | null)?.backTo;
    return isBackTarget(value) ? value : null;
  });

  if (!to) return null;

  const label =
    to === "/dashboard"
      ? t("nav.backToHome")
      : to === "/subjects"
        ? t("nav.backToSubjects")
        : to === "/inventory"
          ? t("common.backToItem", { title: t("inventory.title") })
          : t("common.backToItem", { title: t("nav.overview") });

  return { to, label };
}
